/**
 * PresentationSlideView React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import {withStyles} from "@material-ui/core";
import PropTypes from 'prop-types';
import MediaPanel from "../../base/MediaPanel";
import AnimatedUpdate from "../../animation/AnimatedUpdate";
import {getStandardAnimate, getHorizontalAnimate, getBottomAnimate, getStandardTransition} from "./animationUtils";
import {patchServerUrl} from "../../../config";


const screenRatio = 16 / 9;
const screenHeightVW = screenRatio * 100;


/**
 * generic presentation slide view
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
function PresentationSlideView(props) {

    const {classes, screenIndex, data} = props;
    const topVW = screenIndex ? screenIndex * screenHeightVW : 0;

    const teaserBackgroundStyle = {
        backgroundImage: data.teaserImageUrl && "linear-gradient(180deg, rgba(0, 0, 0, 1.0), rgba(0, 0, 0, 0.2) 15%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 1) 100%), url(" + patchServerUrl(data.teaserImageUrl) + ")",
        backgroundSize: "cover",
        backgroundPosition: "50% 50%",
        backgroundRepeat: "no-repeat",
    };

    const chapterBackgroundStyle = {
        backgroundImage: data.chapterImageUrl && "linear-gradient(180deg, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 15%, rgba(0, 0, 0, 0) 100%), url(" + patchServerUrl(data.chapterImageUrl) + ")",
        backgroundSize: "cover",
        backgroundPosition: "50% 50%",
        backgroundRepeat: "no-repeat",
    };

    return <div style={{transform: "translate(0, " + -topVW + "vw)"}}>
        <div className="presentation-screen">

            <div className={classes.teaserImageSection}>
                <AnimatedUpdate tag={data.teaserImageUrl} animate={getStandardAnimate()} transition={getStandardTransition(0.75)}>
                    <div className={classes.teaserImage} style={teaserBackgroundStyle}/>
                </AnimatedUpdate>
            </div>
            <div className={classes.teaserContentSection}>
                <AnimatedUpdate tag={data.teaserTag} animate={getStandardAnimate()} transition={getStandardTransition(0.5, 0.75)}>
                    <div className={classes.teaserContainer}>
                        {data.teaserElement}
                    </div>
                </AnimatedUpdate>
            </div>
            <div className={classes.titleSection}>
                {data.titleElement}
            </div>

            {props.progressBar && <div className={classes.progressSection}>
                {props.progressBar}
            </div>}

            <div className={classes.chapterImageSection}>
                <AnimatedUpdate tag={data.chapterImageUrl} animate={getBottomAnimate()} transition={getStandardTransition(0.75)}>
                    <div className={classes.chapterImage} style={chapterBackgroundStyle}/>
                </AnimatedUpdate>
            </div>
            <div className={props.progressBar ? classes.chapterContentSection : classes.extendedContentSection}>
                <AnimatedUpdate tag={data.chapterTitle} order={props.index} animate={getHorizontalAnimate()} transition={getStandardTransition(0.5)}>
                    <div className={classes.chapterTitle}>{data.chapterTitle}</div>
                </AnimatedUpdate>
                <AnimatedUpdate tag={data.chapterTag} animate={getStandardAnimate()} transition={getStandardTransition(0, 2)}>
                    {data.chapterElement}
                </AnimatedUpdate>
            </div>

            <div className={classes.upperScreenMargin}>
                <hr className={classes.screenMarginHr}/>
            </div>
            <div className={classes.lowerScreenMargin}>
                <hr className={classes.screenMarginHr}/>
            </div>

            {data.backgroundAudioUrl && <MediaPanel
                style={{ width: 0, height: 0 }}
                url={data.backgroundAudioUrl}
                autoplay={true}
                showControls={false}
                loop={false}
                volume={props.screenIndex == 1 ? 0 : 100}
                otherFilesAsDownload={false}
            />}

        </div>
    </div>;
}


PresentationSlideView.propTypes = {
    screenIndex: PropTypes.number,
    data: PropTypes.object.isRequired,
    index: PropTypes.number,
    progressBar: PropTypes.object
};

const styles = theme => ({
    titleSection: {
        position: "absolute",
        top: "10vw",
        height: "115vw",
        width: "100vw"
    },
    title: {
        marginLeft: "8vw"
    },
    teaserImageSection: {
        position: "absolute",
        top: "50vw",
        height: "100vw",
        width: "100vw"
    },
    teaserImage: {
        width: "100vw",
        height: "100vw"
    },
    teaserContentSection: {
        position: "absolute",
        top: "50vw",
        height: "115vw",
        width: "100vw"
    },
    teaserContainer: {
        width: "100vw",
        height: "115vw"
    },
    progressSection: {
        position: "absolute",
        top: "195vw",
        width: "100%"
    },
    chapterImageSection: {
        position: "absolute",
        top: "230.56vw",
        height: "125vw",
        width: "100vw"
    },
    chapterImage: {
        width: "100vw",
        height: "125vw"
    },
    chapterContentSection: {
        position: "absolute",
        top: "220.56vw",
        height: "135vw",
        width: "100vw"
    },
    extendedContentSection: {
        position: "absolute",
        top: "190.56vw",
        height: "165vw",
        width: "100vw"
    },
    chapterTitle: {
        marginLeft: "8vw",
        marginRight: "8vw",
        fontSize: "4vw"
    },
    upperScreenMargin: {
        position: "absolute",
        top: "177.77vw",
    },
    lowerScreenMargin: {
        position: "absolute",
        top: "355.54vw",
    },
    screenMarginHr: {
        width:"100vw",
        backgroundColor:"#444444",
        height:"1px",
        border:0,
        margin:0
    }
});

export default withStyles(styles)(PresentationSlideView);
